import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  AlertColor,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PageLoader from 'components/PageLoader';
import { MouseEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import strings from '../../../../../common/Translation/Translate';
import DrawerMenu from '../../../../../components/DrawerMenu';
import Form, { FormDataModel } from '../../../../../components/Form';
import Notification from '../../../../../components/Notification';
import axiosInstance from '../../../../../utils/axios';
import { removeErrorFieldsFromValues } from '../../../../../utils/validators';
import { AddPaymentDrawerFormModel, AddPaymentFormModel } from './AddPaymentForm';
import { URLS } from '../../../../../utils/constants/urls';
import { debounce } from 'lodash';

type AddPaymentFormComponentProps = {
  hasError: any;
  paymentMethodList: any;
  handleSave: (e: any) => void;
  formRef: any;
  cashLimit: number;
  setIsBillToCompanySelected: (val: boolean) => void;
  isRefund: boolean;
  formData: any;
  formValues: any;
  onFormChange: (field: string, value: any, formData: any) => void;
  isYcs?: boolean;
  onCancel?: () => void;
  inline?: boolean;
  formLayout?: 'default' | 'drawer';
  isLoading?: boolean;
};

const AddPaymentFormComponent: React.FC<AddPaymentFormComponentProps> = ({
  hasError,
  paymentMethodList,
  handleSave,
  formRef,
  cashLimit = 50000,
  setIsBillToCompanySelected,
  isRefund,
  formData,
  formValues,
  onFormChange,
  isYcs = false,
  onCancel,
  inline = false,
  formLayout = 'default',
  isLoading = false,
}) => {
  const [showPan, setShowPan] = useState(false);
  const handleChange = (field: string, value: any, formData: any) => {
    if (formData.paymentMethod === 'CASH' && formData.amount !== '' && parseInt(formData.amount) >= cashLimit) {
      setShowPan(true);
    } else if (formData.paymentMethod === 'BILL_TO_COMPANY') {
      setIsBillToCompanySelected(true);
    } else {
      setIsBillToCompanySelected(false);
      setShowPan(false);
    }

    onFormChange(field, value, formData);
  };

  const formModel =
    formLayout === 'drawer'
      ? AddPaymentDrawerFormModel(strings, paymentMethodList, showPan, isRefund, formData, isYcs)
      : AddPaymentFormModel(strings, paymentMethodList, showPan, 12, isRefund, formData, isYcs);

  return (
    <>
      <Grid container spacing={formLayout === 'drawer' ? 1.5 : 2} sx={{ my: inline ? 0 : 2 }}>
        <Form
          onChange={handleChange}
          hasError={hasError}
          ref={formRef as RefObject<Form>}
          model={formModel}
          values={formValues}
        />
      </Grid>
      <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', mt: inline ? 2 : 0, justifyContent: 'flex-end'}}>
        {inline && onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            {formLayout === 'drawer' ? strings.BookingDetailButonTextCancel : strings.common_button_back}
          </Button>
        )}
        <Button
          variant="contained"
          color={formLayout === 'drawer' ? 'primary' : undefined}
          sx={{color: '#ffffff', minWidth: 120}}
          disabled={isLoading}
          onClick={e => handleSave(e)}>
          {strings.common_button_save}
        </Button>
      </Box>
    </>
  );
};

export type AddPaymentFormPanelProps = {
  onSuccess: () => void;
  onCancel?: () => void;
  cashLimit: number;
  paymentMethods: any;
  url: string;
  isRefund?: boolean;
  isYcs?: boolean;
  inline?: boolean;
  title?: string;
  formLayout?: 'default' | 'drawer';
};

export const AddPaymentFormPanel: React.FC<AddPaymentFormPanelProps> = ({
  onSuccess,
  onCancel,
  cashLimit,
  paymentMethods,
  url,
  isRefund = false,
  isYcs = false,
  inline = false,
  title,
  formLayout = 'default',
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isBillToCompanySelected, setIsBillToCompanySelected] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [roomNo, setRoomNo] = useState<string>('');
  const [formData, setFormData] = useState<any>({});
  const [formValues, setFormValues] = useState<any>({});
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const formRef: RefObject<Form | null | undefined> = useRef();
  const savingRef = useRef(false);

  const resetForm = () => {
    setHasError(false);
    setRoomNo('');
    setFormData({});
    setFormValues({});
    formRef?.current?.resetForm();
  };

  const handleClose = () => {
    resetForm();
    onCancel?.();
  };

  const handleSaveHelper = async (data: any) => {
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    const requestBody = new FormData();
    requestBody.append('paymentMethod', data.paymentMethod);
    requestBody.append('amount', data.amount);
    requestBody.append('isTdsDeducted', data.isTdsDeducted.toString());
    requestBody.append('paymentReference', data.paymentReference);
    requestBody.append('roomNumber', data.roomNumber || '');
    requestBody.append('roomReservationId', data.roomReservationId || '');
    requestBody.append('roomReservationName', data.roomReservationName || '');
    for (let i = 0; i < data?.attachment?.length; i++) {
      requestBody.append('attachment', data?.attachment[i]);
    }
    if (data.attachment.length === 0) {
      requestBody.append('attachment', null as any);
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    requestBody.append('docNo', parseFloat(data?.amount) >= cashLimit ? data?.docNo?.toUpperCase() || '' : '');

    if (
      data.paymentMethod === 'CASH' &&
      parseFloat(data.amount) >= (cashLimit || 50000) &&
      (data.docNo === undefined || data.docNo === '' || data.attachment?.length === 0)
    ) {
      savingRef.current = false;
      setMessage({
        display: true,
        severity: 'warning',
        message: strings.add_payment_error,
      });
      return false;
    }
    setIsLoading(true);

    try {
      const { status } = await axiosInstance.post(url, requestBody, config);
      if (status === 200) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.common_save_message,
        });
        onSuccess();
        resetForm();
        onCancel?.();
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.something_went_wrong,
        });
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response.data?.error_description
          ? error.response.data.error_description
          : error.response.data.message,
      });
    } finally {
      savingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.preventDefault();
      if (savingRef.current || isLoading) return;
      const { getFormData } = formRef.current as {
        getFormData: () => { formData: FormDataModel; isFormValid: boolean };
      };
      const { formData, isFormValid } = getFormData();
      const data = removeErrorFieldsFromValues(formData) as any;
      setHasError(false);
      if (isFormValid) {
        if (isBillToCompanySelected && data.attachment.length === 0) {
          setMessage({
            display: true,
            severity: 'error',
            message: strings.uploadHotelInvoice,
          });
        } else if (data.amount === '0') {
          setMessage({
            display: true,
            severity: 'error',
            message: strings.amountGreaterThanZero,
          });
        } else {
          handleSaveHelper(data);
        }
      } else {
        setHasError(true);
      }
    } catch (error: any) {
      savingRef.current = false;
      setIsLoading(false);
      if (error.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description ?? error.response.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    }
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const fetchRoomReservations = useCallback(
    debounce(async (roomNo: string) => {
      try {
        setIsLoading(true);
        const { status, data } = await axiosInstance.post(URLS.fetchReservations(roomNo));
        if ([200, 201].includes(status)) {
          setFormData((prev: any) => ({
            ...prev,
            roomNumber: data.roomNumber ?? roomNo,
            roomReservationId: data.roomReservationId ?? 'N/A',
            roomReservationName: data.roomReservationName ?? 'N/A',
          }));
          setFormValues((prev: any) => ({
            ...prev,
            roomReservationId: data.roomReservationId ?? 'N/A',
            roomReservationName: data.roomReservationName ?? 'N/A',
          }));
        }
      } catch (error: any) {
        setMessage({
          display: true,
          severity: 'error',
          message:
            error.response?.data?.error_description ??
            error.response?.data?.message ??
            error.message ??
            'Something went wrong',
        });
      } finally {
        setIsLoading(false);
      }
    }, 1000),
    [],
  );

  useEffect(() => {
    if (roomNo) {
      fetchRoomReservations(roomNo);
    }
  }, [fetchRoomReservations, roomNo]);

  const handleFormChange = (field: string, value: any) => {
    setFormValues((prev: any) => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'roomNumber') {
      setRoomNo(value);
    }
  };

  const panelTitle =
    title ?? (isRefund ? 'Process Refund' : strings.bookingDetailsAddPayment_newButton);

  const formContent = (
    <AddPaymentFormComponent
      cashLimit={cashLimit}
      hasError={hasError}
      paymentMethodList={paymentMethods}
      handleSave={handleSave}
      setIsBillToCompanySelected={setIsBillToCompanySelected}
      formRef={formRef}
      isRefund={isRefund}
      formData={formData}
      formValues={formValues}
      onFormChange={handleFormChange}
      isYcs={isYcs}
      onCancel={handleClose}
      inline={inline}
      formLayout={formLayout}
      isLoading={isLoading}
    />
  );

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      {inline && formLayout === 'drawer' ? (
        <Box sx={{pt: 2, borderTop: 1, borderColor: 'divider'}}>{formContent}</Box>
      ) : inline ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
            borderColor: isRefund ? 'error.light' : 'primary.light',
          }}>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5}}>
            <Typography variant="subtitle2" sx={{fontWeight: 700}}>
              {panelTitle}
            </Typography>
            {onCancel && (
              <IconButton size="small" onClick={handleClose} aria-label="close">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {formContent}
        </Paper>
      ) : (
        formContent
      )}
    </>
  );
};

type AddPaymentButtonProps = {
  onSuccess: () => void;
  cashLimit: number;
  paymentMethods: any;
  url: string;
  text?: string;
  color?: any;
  variant?: 'contained' | 'outlined';
  size?: 'small' | 'medium';
  isYcs?: boolean;
};

export const AddPaymentButton: React.FC<AddPaymentButtonProps> = ({
  onSuccess,
  cashLimit,
  paymentMethods,
  url,
  text,
  color: btnClass,
  variant = 'contained',
  size = 'medium',
  isYcs = false,
}) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const isRefund = btnClass === 'error';

  const handleClose = () => {
    setOpenDrawer(false);
  };

  const handleSuccess = () => {
    onSuccess();
    setOpenDrawer(false);
  };

  return (
    <>
      <Tooltip
        title={
          text
            ? text
            : isRefund
              ? strings.bookingDetailsProcessRefund ?? 'Process Refund'
              : strings.bookingDetailsAddPayment_newButton
        }
        placement="top"
        arrow>
        <span>
          <Button
            style={{ textTransform: 'none' }}
            onClick={() => {
              if (isRefund) {
                setConfirmOpen(true);
              } else {
                setOpenDrawer(true);
              }
            }}
            variant={variant}
            color={btnClass ? btnClass : 'success'}
            size={size}
            sx={{ minHeight: size === 'small' ? '32px' : '36px', my: 1 }}
            startIcon={isRefund ? <ReplayIcon /> : <AddIcon />}>
            {text ? text : strings.bookingDetailsAddPayment_newButton}
          </Button>
        </span>
      </Tooltip>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} aria-labelledby="refund-confirm-title">
        <DialogTitle id="refund-confirm-title">{'Confirm refund'}</DialogTitle>
        <DialogContent>
          <DialogContentText>{'You are about to process a refund. Do you want to continue?'}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{strings.common_button_back}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              setOpenDrawer(true);
            }}>
            {text || 'Process Refund'}
          </Button>
        </DialogActions>
      </Dialog>
      <DrawerMenu
        width={screenSizeDownSm ? '100%' : 420}
        open={openDrawer}
        handleClose={handleClose}
        closeButton={true}
        title={isRefund ? 'Process Refund' : strings.bookingDetailsAddPayment_newButton}>
        <AddPaymentFormPanel
          onSuccess={handleSuccess}
          onCancel={handleClose}
          cashLimit={cashLimit}
          paymentMethods={paymentMethods}
          url={url}
          isRefund={isRefund}
          isYcs={isYcs}
        />
      </DrawerMenu>
    </>
  );
};
